import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle2,
  Upload as UploadIcon,
  X as XIcon,
  ClipboardCheck,
  MessagesSquare,
  Beaker,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Honeypot from "@/components/Honeypot";
import { siteConfig } from "@/config/siteConfig";
import { COUNTRIES } from "@/data/countries";
import { products } from "@/data/productsData";
import { submitLead, readAttribution, baseMeta, uploadFile } from "@/lib/api";
import { trackLeadSubmit } from "@/lib/analytics";
import TurnstileWidget, { useSubmitBlockedByTurnstile } from "@/components/TurnstileWidget";

const PRINTING_TECHNOLOGIES = ["TIJ", "CIJ", "Flexographic", "Screen", "Gravure", "Rotogravure", "Offset", "Other"];
const INDUSTRIES = ["Pharmaceutical Manufacturing", "Food Manufacturing", "Other"];

const PRODUCT_OPTIONS = [
  ...products.map((p) => ({ value: p.slug, label: p.name })),
  { value: "other", label: "Other / Not sure — please advise" },
];

const STEPS = [
  { key: "details", num: 1, label: "Your Details" },
  { key: "application", num: 2, label: "Application" },
  { key: "submit", num: 3, label: "Submit Request" },
];

const TRUST_ITEMS = [
  { Icon: ClipboardCheck, title: "Application Reviewed", caption: "Machine, substrate and requirement checked before we recommend." },
  { Icon: MessagesSquare, title: "Product Suitability Discussed", caption: "Suitable product identified for your application." },
  { Icon: Beaker, title: "Sample Evaluation Where Appropriate", caption: "Trial the product under your production conditions." },
];

const initial = {
  company: "",
  country: "",
  city: "",
  contactName: "",
  position: "",
  email: "",
  phone: "",
  industry: "",
  productRequired: "",
  machineBrand: "",
  machineModel: "",
  printingTechnology: "",
  substrate: "",
  currentInk: "",
  currentSupplier: "",
  monthlyConsumption: "",
  currentIssue: "",
  preferredContact: "email",
  consent: false,
};

const Label = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5">
    {children} {required && <span className="text-[var(--pharma-blue)]">*</span>}
  </label>
);
const inputCls =
  "w-full min-h-[44px] border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";

const FileField = ({ id, label, file, onChange, testid }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-3">
      <label
        htmlFor={id}
        className="inline-flex items-center gap-2 border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[13px] text-[var(--charcoal)] bg-white cursor-pointer hover:bg-[var(--bg-alt)] min-h-[44px]"
      >
        <UploadIcon className="w-4 h-4" strokeWidth={1.5} />
        Choose file
      </label>
      <input
        id={id}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testid}
      />
      {file && (
        <span className="text-[12.5px] text-[var(--slate)] inline-flex items-center gap-2 max-w-[240px] truncate">
          <span className="truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove file"
            className="text-[var(--slate)] hover:text-[var(--navy)]"
            data-testid={`${testid}-remove`}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </div>
    <p className="mt-1.5 text-[11.5px] text-[var(--slate)]">JPG, PNG, WEBP, GIF or PDF, up to 10 MB.</p>
  </div>
);

const WizardSteps = ({ current }) => (
  <ol className="wizard-steps" data-testid="rs-wizard-steps" aria-label="Sample request steps">
    {STEPS.map((s) => {
      const state = s.num < current ? "done" : s.num === current ? "active" : "todo";
      return (
        <li key={s.key} className="wizard-step" data-state={state} data-testid={`rs-step-indicator-${s.num}`}>
          <span className="wizard-step-num">{s.num}</span>
          <span className="wizard-step-label font-heading font-semibold text-[13.5px]">{s.label}</span>
        </li>
      );
    })}
  </ol>
);

const RequestSample = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [hp, setHp] = useState("");
  const [labelFile, setLabelFile] = useState(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
  const inflightRef = useRef(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = params.get("product");
    const ind = params.get("industry");
    setForm((f) => ({
      ...f,
      productRequired: q && products.find((p) => p.slug === q) ? q : f.productRequired,
      industry:
        ind === "food"
          ? "Food Manufacturing"
          : ind === "pharmaceutical"
          ? "Pharmaceutical Manufacturing"
          : f.industry,
    }));
  }, [params]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const attribution = useMemo(() => readAttribution(params), [params]);

  // ---- Per-step validation ----
  const step1Valid =
    form.company.trim() &&
    form.country.trim() &&
    form.contactName.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.industry.trim();
  const step2Valid =
    form.productRequired.trim() &&
    form.substrate.trim() &&
    form.currentIssue.trim();
  const canSubmit = step1Valid && step2Valid && form.consent;

  const goNext = () => {
    setError(null);
    if (step === 1 && !step1Valid) {
      setError("Please complete the required fields marked with an asterisk.");
      return;
    }
    if (step === 2 && !step2Valid) {
      setError("Please complete the required fields marked with an asterisk.");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflightRef.current) return;
    if (!canSubmit) {
      setError("Please complete all required fields and accept the consent statement.");
      return;
    }
    inflightRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      let uploadedLabelId = null;
      let uploadedSampleId = null;
      if (labelFile) {
        const up = await uploadFile(labelFile);
        if (!up.ok) throw new Error(up.error || "Label upload failed");
        uploadedLabelId = up.data.id;
      }
      if (sampleFile) {
        const up = await uploadFile(sampleFile);
        if (!up.ok) throw new Error(up.error || "Sample upload failed");
        uploadedSampleId = up.data.id;
      }
      const body = {
        type: "SAMPLE",
        source_page: "/request-sample",
        contact: {
          name: form.contactName,
          company: form.company,
          position: form.position || null,
          email: form.email,
          phone: form.phone || null,
          country: form.country,
          city: form.city || null,
          preferred_contact_method: form.preferredContact || null,
        },
        attribution,
        meta: baseMeta({ consent_ack: true }),
        payload: {
          product_required: form.productRequired,
          industry: form.industry,
          machine_brand: form.machineBrand || null,
          machine_model: form.machineModel || null,
          printing_technology: form.printingTechnology || null,
          substrate: form.substrate,
          current_ink_or_cartridge: form.currentInk || null,
          current_supplier: form.currentSupplier || null,
          monthly_consumption: form.monthlyConsumption || null,
          current_issue_or_requirement: form.currentIssue,
          consent_ack: true,
          uploaded_label_id: uploadedLabelId,
          uploaded_sample_id: uploadedSampleId,
        },
        _hp: hp,
        turnstile_token: captchaToken,
      };
      const res = await submitLead(body);
      if (res.ok) {
        trackLeadSubmit("sample_request");
        setResult(res.data);
      }
      else if (res.status === 403) setError("Verification failed. Please retry the challenge.");
      else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
      else if (res.status === 422) setError("Please check the required fields and try again.");
      else setError("Something went wrong. Please try again shortly.");
    } catch (err) {
      setError(err.message || "Upload or submission failed. Please try again.");
    } finally {
      setSubmitting(false);
      inflightRef.current = false;
    }
  };

  return (
    <>
      <SEO
        rawTitle={`Request a Product Sample | ${siteConfig.COMPANY_NAME}`}
        description="Tell us about your application and we'll review whether an appropriate product is available for evaluation."
        path="/request-sample"
      />
      <Helmet />

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Request a Sample" }]} />
      </div>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-6" data-testid="rs-hero">
        <p className="eyebrow">Sample Request</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[48px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
          data-testid="rs-h1"
        >
          Request a Product Sample
        </h1>
        <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)] max-w-3xl" data-testid="rs-subhead">
          Tell us about your application and we&rsquo;ll review whether an appropriate product is available for evaluation.
        </p>

        {/* Trust strip (§10) */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="rs-trust-strip">
          {TRUST_ITEMS.map(({ Icon, title, caption }) => (
            <div
              key={title}
              className="border border-[var(--border)] rounded-[4px] p-5 bg-white flex gap-4 items-start"
              data-testid={`rs-trust-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-6 h-6 text-[var(--pharma-blue)] shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <div>
                <p className="font-heading text-[15px] font-semibold text-[var(--navy)]">{title}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-[var(--slate)]">{caption}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-8 lg:py-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,720px)_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
          {result ? (
            <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-10 bg-[var(--bg-alt)]" data-testid="rs-success">
              <CheckCircle2 strokeWidth={1.5} style={{ width: 44, height: 44, color: "var(--pharma-blue)" }} aria-hidden="true" />
              <h2 className="font-heading mt-4 text-2xl font-semibold text-[var(--navy)]">
                Thank you. Your application details have been received and will be reviewed by our team.
              </h2>
              <p className="mt-4 text-[14.5px] text-[var(--charcoal)]">
                We will get back to you shortly with next steps for sample availability, technical review or a follow-up question.
              </p>
              <p className="mt-6 text-[12px] text-[var(--slate)]" data-testid="rs-reference-id">
                Reference ID: {result.id}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="w-full max-w-[720px]" data-testid="rs-form" noValidate>
              <WizardSteps current={step} />

              <div className="mt-6 border border-[var(--border)] rounded-[4px] p-6 lg:p-8 bg-white">
                {error && (
                  <div
                    className="mb-6 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]"
                    data-testid="rs-error"
                  >
                    {error}
                  </div>
                )}

                {/* STEP 1 */}
                {step === 1 && (
                  <div data-testid="rs-step-1">
                    <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Your Details</h2>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="rs-company" required>Company Name</Label>
                        <input id="rs-company" name="company" required className={inputCls} value={form.company} onChange={onChange} data-testid="rs-company" />
                      </div>
                      <div>
                        <Label htmlFor="rs-country" required>Country</Label>
                        <input list="rs-country-list" id="rs-country" name="country" required className={inputCls} value={form.country} onChange={onChange} data-testid="rs-country" placeholder="Type to search…" />
                        <datalist id="rs-country-list">
                          {COUNTRIES.map(([code, name]) => (<option key={code} value={name} />))}
                        </datalist>
                      </div>
                      <div>
                        <Label htmlFor="rs-city">City</Label>
                        <input id="rs-city" name="city" className={inputCls} value={form.city} onChange={onChange} data-testid="rs-city" />
                      </div>
                      <div>
                        <Label htmlFor="rs-contactName" required>Contact Name</Label>
                        <input id="rs-contactName" name="contactName" required className={inputCls} value={form.contactName} onChange={onChange} data-testid="rs-contactName" />
                      </div>
                      <div>
                        <Label htmlFor="rs-position">Position</Label>
                        <input id="rs-position" name="position" className={inputCls} value={form.position} onChange={onChange} data-testid="rs-position" />
                      </div>
                      <div>
                        <Label htmlFor="rs-email" required>Business Email</Label>
                        <input id="rs-email" name="email" type="email" required className={inputCls} value={form.email} onChange={onChange} data-testid="rs-email" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-phone" required>Phone / WhatsApp</Label>
                        <input id="rs-phone" name="phone" required className={inputCls} value={form.phone} onChange={onChange} data-testid="rs-phone" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-industry" required>Industry</Label>
                        <select id="rs-industry" name="industry" required className={inputCls} value={form.industry || ""} onChange={onChange} data-testid="rs-industry">
                          <option value="">Select industry…</option>
                          {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div data-testid="rs-step-2">
                    <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Your Application</h2>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-productRequired" required>Product Required</Label>
                        <select id="rs-productRequired" name="productRequired" required className={inputCls} value={form.productRequired || ""} onChange={onChange} data-testid="rs-productRequired">
                          <option value="">Select a product…</option>
                          {PRODUCT_OPTIONS.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="rs-machineBrand">Machine Brand</Label>
                        <input id="rs-machineBrand" name="machineBrand" className={inputCls} value={form.machineBrand} onChange={onChange} data-testid="rs-machineBrand" />
                      </div>
                      <div>
                        <Label htmlFor="rs-machineModel">Machine Model</Label>
                        <input id="rs-machineModel" name="machineModel" className={inputCls} value={form.machineModel} onChange={onChange} data-testid="rs-machineModel" />
                      </div>
                      <div>
                        <Label htmlFor="rs-printingTechnology">Printing Technology</Label>
                        <select id="rs-printingTechnology" name="printingTechnology" className={inputCls} value={form.printingTechnology} onChange={onChange} data-testid="rs-printingTechnology">
                          <option value="">Select technology…</option>
                          {PRINTING_TECHNOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="rs-substrate" required>Substrate / Material</Label>
                        <input id="rs-substrate" name="substrate" required className={inputCls} value={form.substrate} onChange={onChange} data-testid="rs-substrate" />
                      </div>
                      <div>
                        <Label htmlFor="rs-currentInk">Current Ink or Cartridge</Label>
                        <input id="rs-currentInk" name="currentInk" className={inputCls} value={form.currentInk} onChange={onChange} data-testid="rs-currentInk" />
                      </div>
                      <div>
                        <Label htmlFor="rs-currentSupplier">Current Supplier</Label>
                        <input id="rs-currentSupplier" name="currentSupplier" className={inputCls} value={form.currentSupplier} onChange={onChange} data-testid="rs-currentSupplier" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-monthlyConsumption">Approximate Monthly Consumption</Label>
                        <input id="rs-monthlyConsumption" name="monthlyConsumption" className={inputCls} value={form.monthlyConsumption} onChange={onChange} data-testid="rs-monthlyConsumption" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-currentIssue" required>Current Issue / Requirement</Label>
                        <textarea id="rs-currentIssue" name="currentIssue" required rows={6} className={inputCls} value={form.currentIssue} onChange={onChange} data-testid="rs-currentIssue" />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <div data-testid="rs-step-3">
                    <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Sample &amp; Documents</h2>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FileField id="rs-label-upload" label="Upload Existing Product Label" file={labelFile} onChange={setLabelFile} testid="rs-label-upload" />
                      <FileField id="rs-sample-upload" label="Upload Print Sample / Image" file={sampleFile} onChange={setSampleFile} testid="rs-sample-upload" />
                      <div className="sm:col-span-2">
                        <Label htmlFor="rs-preferredContact">Preferred Contact Method</Label>
                        <div id="rs-preferredContact" className="flex flex-wrap gap-4 mt-1">
                          {["email", "phone", "whatsapp"].map((v) => (
                            <label key={v} className="inline-flex items-center gap-2 text-[14px]">
                              <input type="radio" name="preferredContact" value={v} checked={form.preferredContact === v} onChange={onChange} data-testid={`rs-pref-${v}`} />
                              <span className="capitalize">{v}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="inline-flex items-start gap-3 text-[13.5px] text-[var(--charcoal)]">
                          <input
                            type="checkbox"
                            name="consent"
                            checked={form.consent}
                            onChange={onChange}
                            required
                            data-testid="rs-consent"
                            className="mt-0.5"
                          />
                          <span>
                            I understand that product suitability must be established through appropriate testing under actual production conditions. <span className="text-[var(--pharma-blue)]">*</span>
                          </span>
                        </label>
                      </div>
                    </div>
                    <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
                  </div>
                )}

                {/* Nav buttons */}
                <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      className="btn-outline text-sm inline-flex items-center gap-2"
                      data-testid="rs-back"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={goNext}
                      className="btn-primary text-sm inline-flex items-center gap-2"
                      data-testid="rs-next"
                    >
                      {step === 1 ? "Next — Application Details" : "Next — Sample Information"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting || !canSubmit || captchaBlocks}
                      className="btn-primary text-sm disabled:opacity-70"
                      data-testid="rs-submit"
                    >
                      {submitting ? "Submitting…" : "Submit Sample Request"}
                    </button>
                  )}
                </div>
                {step === 3 && (
                  <div className="pt-2">
                    <TurnstileWidget onToken={setCaptchaToken} action="sample_request" testid="rs-turnstile" />
                  </div>
                )}
              </div>
            </form>
          )}

          <aside className="lg:sticky lg:top-32 border border-[var(--border)] rounded-[4px] p-6 bg-white" data-testid="rs-info">
            <p className="font-heading text-[12px] font-semibold uppercase text-[var(--slate)]" style={{ letterSpacing: "0.12em" }}>
              What happens next
            </p>
            <ol className="mt-4 space-y-4 text-[14.5px] text-[var(--charcoal)]">
              <li className="flex gap-3">
                <span className="font-heading font-semibold text-[13px] w-6 h-6 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: "var(--pharma-blue)", color: "var(--navy)" }}>01</span>
                Technical review of your application details.
              </li>
              <li className="flex gap-3">
                <span className="font-heading font-semibold text-[13px] w-6 h-6 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: "var(--pharma-blue)", color: "var(--navy)" }}>02</span>
                Sample availability confirmation and quantity discussion.
              </li>
              <li className="flex gap-3">
                <span className="font-heading font-semibold text-[13px] w-6 h-6 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: "var(--pharma-blue)", color: "var(--navy)" }}>03</span>
                Guidance for a controlled sample evaluation and production trial before regular supply.
              </li>
            </ol>
            <p className="mt-6 text-[13px] text-[var(--slate)]">
              Prefer email? Reach us at{" "}
              <a href={`mailto:${siteConfig.EMAIL_SALES}`} className="underline text-[var(--pharma-blue)]">
                {siteConfig.EMAIL_SALES}
              </a>.
            </p>
            <p className="mt-6 text-[12px] italic text-[var(--slate)]">
              Product suitability is confirmed through application review.
            </p>
            <div className="mt-6">
              <Link to="/contact" className="arrow-link text-[13px]" data-testid="rs-contact-link">Contact our team</Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default RequestSample;
